import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useDndContext } from '@dnd-kit/core';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { getProjection } from "./utilities.js";
import { SortableTreeItem } from "./SortableTreeItem.js";
import { TreeItem } from "./TreeItem.js";
import { jsx as _jsx } from "react/jsx-runtime";
export default function SortableTree(_ref) {
  var _ref2, _dndContext$active, _ref3, _dndContext$over, _dndContext$active$re, _dndContext$active$re2, _dndContext$active$re3, _dndContext$active$re4;
  var items = _ref.items,
    _ref$indentationWidth = _ref.indentationWidth,
    indentationWidth = _ref$indentationWidth === void 0 ? 30 : _ref$indentationWidth,
    renderItem = _ref.renderItem,
    _ref$isDraggable = _ref.isDraggable,
    isDraggable = _ref$isDraggable === void 0 ? true : _ref$isDraggable,
    _ref$withDepthMarkers = _ref.withDepthMarkers,
    withDepthMarkers = _ref$withDepthMarkers === void 0 ? true : _ref$withDepthMarkers,
    virtualizerRef = _ref.virtualizerRef;
  var dndContext = useDndContext();
  var activeId = (_ref2 = (_dndContext$active = dndContext.active) === null || _dndContext$active === void 0 ? void 0 : _dndContext$active.id) !== null && _ref2 !== void 0 ? _ref2 : null;
  var overId = (_ref3 = (_dndContext$over = dndContext.over) === null || _dndContext$over === void 0 ? void 0 : _dndContext$over.id) !== null && _ref3 !== void 0 ? _ref3 : null;
  var offsetLeft = dndContext.active ? ((_dndContext$active$re = (_dndContext$active$re2 = dndContext.active.rect.current.translated) === null || _dndContext$active$re2 === void 0 ? void 0 : _dndContext$active$re2.left) !== null && _dndContext$active$re !== void 0 ? _dndContext$active$re : 0) - ((_dndContext$active$re3 = (_dndContext$active$re4 = dndContext.active.rect.current.initial) === null || _dndContext$active$re4 === void 0 ? void 0 : _dndContext$active$re4.left) !== null && _dndContext$active$re3 !== void 0 ? _dndContext$active$re3 : 0) : 0;
  var filteredItems = useMemo(() => {
    if (activeId != null) {
      return items.filter(_ref4 => {
        var id = _ref4.id,
          selected = _ref4.selected;
        return id === activeId || !selected;
      });
    }
    return items;
  }, [activeId, items]);
  var activeIndex = useMemo(() => filteredItems.findIndex(_ref5 => {
    var id = _ref5.id;
    return id === activeId;
  }), [activeId, filteredItems]);

  // Add the active index to the range so it is always rendered
  var rangeExtractor = useCallback(range => {
    var extractedRange = defaultRangeExtractor(range);
    if (activeIndex >= 0 && (activeIndex < extractedRange[0] || activeIndex > extractedRange[extractedRange.length - 1])) {
      extractedRange.push(activeIndex);
    }
    return extractedRange;
  }, [activeIndex]);
  var getScrollElement = useCallback(() => {
    var _containerRef$current, _containerRef$current2;
    return (_containerRef$current = (_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.parentElement) !== null && _containerRef$current !== void 0 ? _containerRef$current : null;
  }, []);
  var estimateSize = useCallback(() => 30, []);
  var containerRef = useRef(null);
  var virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement,
    estimateSize,
    rangeExtractor,
    overscan: 10,
    gap: 1 // We set a 1px bottom margin before we virtualized this list
  });

  // Expose the virtualizer via ref
  useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer]);
  var virtualizedItems = virtualizer.getVirtualItems();
  var _useDndContext = useDndContext(),
    measureDroppableContainers = _useDndContext.measureDroppableContainers;

  // Without this, animations are funky when using the move/sort buttons
  // dnd-kit only remeasures on drag/drop by default
  useEffect(function remeasureContainers() {
    measureDroppableContainers(virtualizedItems.map(_ref6 => {
      var index = _ref6.index;
      return filteredItems[index].id;
    }));
  }, [filteredItems, measureDroppableContainers, virtualizedItems]);
  var projected = isDraggable && activeId != null && overId != null ? getProjection(filteredItems, activeId, overId, offsetLeft, indentationWidth) : null;
  return /*#__PURE__*/_jsx("div", {
    className: classNames('tree-container', activeId != null && 'marching-ants'),
    style: {
      height: "".concat(virtualizer.getTotalSize(), "px"),
      position: 'relative'
    },
    ref: containerRef,
    children: virtualizer.getVirtualItems().map(_ref7 => {
      var index = _ref7.index,
        start = _ref7.start;
      var item = filteredItems[index];
      var id = item.id,
        depth = item.depth;
      return isDraggable ? /*#__PURE__*/_jsx(SortableTreeItem, {
        id: id,
        value: id,
        depth: id === activeId && projected ? projected.depth : depth,
        item: item,
        renderItem: renderItem,
        withDepthMarkers: withDepthMarkers,
        top: start
        // This allows the group items to expand when editing the name and shift the list correctly
        ,
        measureElement: virtualizer.measureElement
      }, id) : /*#__PURE__*/_jsx(TreeItem, {
        value: id,
        depth: depth,
        item: item,
        renderItem: renderItem,
        withDepthMarkers: withDepthMarkers,
        top: start,
        wrapperRef: virtualizer.measureElement
      }, id);
    })
  });
}
//# sourceMappingURL=SortableTree.js.map